import * as React from 'react';
import jsPDF from "jspdf";
import moment from 'moment'; // Install this if you haven't: npm install dayjs
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import autoTable from "jspdf-autotable";
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';

import { t } from 'i18next';
import { MenuItem, Select, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import toast from 'react-hot-toast';
import styles from './InvoiceUi.module.css';
import { SortIcon } from '../../../../assets/images/icons';
import { fetchInvoices } from '../../../../services/paymentService';
import { InvoiceFullView } from './InvoiceFullView';
import { getCurrencySymbol } from '../../../../utils/formatHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { setDisableHeader, setInvoices } from '../../../../app/features/userSlice';
import { useNavigate } from 'react-router-dom';

// Helper function to adapt invoice data to a consistent format
const normalizeInvoice = (invoice) => {
    // Check if it's a database invoice or Stripe invoice
    const isStripeInvoice = !invoice.invoiceNumber && invoice.number;
    
    if (isStripeInvoice) {
        // It's a Stripe invoice, normalize it
        return {
            id: invoice.id,
            number: invoice.number,
            invoiceNumber: invoice.number,
            created: invoice.created,
            createdAt: new Date(invoice.created * 1000),
            due_date: invoice.due_date,
            dueDate: invoice.due_date ? new Date(invoice.due_date * 1000) : null,
            total: invoice.total,
            amount: invoice.total / 100,
            currency: invoice.currency,
            paid: invoice.paid,
            status: invoice.status,
            customer_email: invoice.customer_email,
            userEmail: invoice.customer_email,
            customer_address: invoice.customer_address,
            billingAddress: invoice.customer_address,
            lines: invoice.lines,
            subscription: invoice.subscription,
            status_transitions: invoice.status_transitions,
            paidAt: invoice.status_transitions?.paid_at ? new Date(invoice.status_transitions.paid_at * 1000) : null,
            isStripeInvoice: true
        };
    } else {
        // It's already a database invoice, just add some compatibility fields
        return {
            ...invoice,
            number: invoice.invoiceNumber,
            created: invoice.createdAt ? Math.floor(new Date(invoice.createdAt).getTime() / 1000) : null,
            due_date: invoice.dueDate ? Math.floor(new Date(invoice.dueDate).getTime() / 1000) : null,
            total: invoice.amount * 100,
            paid: invoice.status === 'paid',
            customer_email: invoice.userEmail,
            customer_address: invoice.billingAddress,
            isStripeInvoice: false
        };
    }
};

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;

    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    const headCells = [
        { id: 'number', numeric: false, disablePadding: true, label: t('invoiceTable.invoiceNumber') },
        { id: 'created', numeric: true, disablePadding: false, label: t('invoiceTable.invoiceDate') },
        { id: 'due_date', numeric: true, disablePadding: false, label: t('invoiceTable.dueDate') },
        { id: 'total', numeric: true, disablePadding: false, label: t('invoiceTable.total') },
        { id: 'status', numeric: true, disablePadding: false, label: t('invoiceTable.status') },
    ];

    return (
        <TableHead
            style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1,
            }}
        >
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align='center'
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{
                            borderBottom: orderBy === headCell.id ? '3px solid green' : '3px solid lightgreen',
                            padding: '5px',
                            width: 'auto',
                            whiteSpace: 'nowrap',
                            fontSize: '0.9rem',
                            '&:hover': {
                                backgroundColor: '#f6f6f6',
                            },
                        }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            IconComponent={() => (
                                headCell.id === "status" ?
                                    // null 
                                    <img src={SortIcon} alt={'SortIcon'} className={styles.sortIcon} />
                                    :
                                    <img src={SortIcon} alt={'SortIcon'} className={styles.sortIcon} />
                            )}
                        >
                            {headCell.label}
                            {orderBy === headCell.id && (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            )}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const InvoiceUi = ({ invoices, selectedInvoiceData }) => {
    const [page, setPage] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [order, setOrder] = React.useState('asc');
    const [dense, setDense] = React.useState(false);
    const [orderBy, setOrderBy] = React.useState('created');
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [selectedStatus, setSelectedStatus] = React.useState('');
    const [selectedInvoice, setSelectedInvoice] = React.useState(null);

    const user = useSelector(state => state.user.user);
    
    // Normalize all invoices to a consistent format
    const normalizedInvoices = React.useMemo(() => {
        return invoices?.map(invoice => normalizeInvoice(invoice)) || [];
    }, [invoices]);
    
    React.useEffect(() => {
        if (selectedInvoiceData && Object.keys(selectedInvoiceData)?.length > 0) {
            setSelectedInvoice(normalizeInvoice(selectedInvoiceData)); // Set the selected invoice for full view
            dispatch(setDisableHeader(true))
        }
    }, [selectedInvoiceData])

    const totalCount = useSelector(state => state.user.totalCount);
    const email = useSelector(state => state.user.user.email);

    // Simplified rows per page options
    const rowsPerPageOptions = [10, 20, 30, 50];

    const getInvoices = async (email, limit, offset = 0) => {
        try {
            setLoading(true);
            toast.promise(fetchInvoices(email, limit, offset), {
                loading: t('general.UI.loading'),
                success: <b>{t('general.UI.success')}</b>,
                error: (err) => {
                    return <b>{err.error}</b>;
                },
            })
            .then((response) => {
                if (response.userInvoices) {
                    dispatch(setInvoices({ 
                        fetchedInvoices: response.userInvoices, 
                        totalCount: response.totalCount 
                    }));
                }
                setLoading(false);
            })
            .catch((err) => {
                toast.error(t('general.UI.something_went_wrong'));
                setLoading(false);
            });
        } catch (err) {
            setError(err);
            setLoading(false);
        }
    };

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const handleRowClick = (invoice) => {
        dispatch(setDisableHeader(true))
        setSelectedInvoice(invoice); // Set the selected invoice for full view
        navigate(`/invoice/${invoice?.number?.slice(0)}`)
    };

    const handleCloseFullView = () => {
        dispatch(setDisableHeader(false))
        navigate(`/profile/billing-history`)
        setSelectedInvoice(null); // Reset when closing the full view
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = async (event, newPage) => {
        if (!loading) {
            setLoading(true);
            const offset = newPage * rowsPerPage;
            
            // Always fetch data when changing pages to ensure we have the right data
            await getInvoices(email, rowsPerPage, offset);
            
            setPage(newPage);
        }
    };
    
    const handleChangeRowsPerPage = async (event) => {
        if (!loading) {
            setLoading(true);
            const newRowsPerPage = parseInt(event.target.value, 10);
            
            // Reset to first page and fetch with new limit
            await getInvoices(email, newRowsPerPage, 0);
            
            setRowsPerPage(newRowsPerPage);
            setPage(0);
        }
    };

    const visibleRows = React.useMemo(
        () =>
            normalizedInvoices
                ?.filter(row => {
                    if (selectedStatus === '') return true;
                    if (selectedStatus === 'paid') return row.paid || row.status === 'paid';
                    if (selectedStatus === 'unpaid') return (!row.paid && row.status !== 'void') || row.status === 'unpaid';
                    if (selectedStatus === 'cancel') return row.status === 'void';
                    if (selectedStatus === 'refunded') return row.status === 'refunded';
                    return true;
                })
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, page, rowsPerPage, selectedStatus, normalizedInvoices],
    );
    
    // Use totalCount for pagination
    const filteredCount = totalCount || 0;

    const handleDownloadInvoice = async (invoice) => {
        try {
            const doc = new jsPDF();

            // Add Logo or Header
            doc.setFontSize(18);
            doc.text("Findcv.com", 14, 20);
            doc.setFontSize(12);
            doc.text("123 Main St, City, Country", 14, 30);
            doc.text("Email: support@findcv.com", 14, 35);

            // Invoice Details
            doc.setFontSize(16);
            doc.text("INVOICE", 180, 20, { align: "right" });
            doc.setFontSize(12);

            // Adjust for overflow and align right
            const invoiceNumberText = `Invoice Number: ${invoice.invoiceNumber || invoice.number || "N/A"}`;
            doc.text(invoiceNumberText, 180, 30, { align: "right" });

            const createdDate = invoice.isStripeInvoice 
                ? moment.unix(invoice.created).format("MMMM Do, YYYY")
                : moment(invoice.createdAt).format("MMMM Do, YYYY");
                
            doc.text(`Date Issued: ${createdDate}`, 180, 35, { align: "right" });
            
            const dueDate = invoice.isStripeInvoice && invoice.due_date 
                ? moment.unix(invoice.due_date).format("MMMM Do, YYYY") 
                : invoice.dueDate 
                    ? moment(invoice.dueDate).format("MMMM Do, YYYY") 
                    : "No Due Date";
                    
            doc.text(`Due Date: ${dueDate}`, 180, 40, { align: "right" });

            // Customer Details
            const customer = invoice.userEmail || invoice.customer_email || "N/A";
            doc.text(`Customer: ${customer}`, 14, 50);

            // Line Items Table
            const lineItems = [];
            
            if (invoice.isStripeInvoice && invoice.lines?.data) {
                // For Stripe invoices
                lineItems.push(...invoice.lines.data.map((item) => [
                    item.description || "N/A",
                    item.quantity || 1,
                    `${item.currency.toUpperCase()} ${(item.amount / 100).toFixed(2)}`,
                ]));
            } else {
                // For database invoices
                lineItems.push([
                    invoice.planDescription || invoice.planName || "Subscription Service",
                    invoice.quantity || 1,
                    `${invoice.currency.toUpperCase()} ${Number(invoice.amount).toFixed(2)}`,
                ]);
            }

            doc.autoTable({
                startY: 60,
                head: [["Description", "Quantity", "Amount"]],
                body: lineItems,
                theme: "striped",
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                bodyStyles: { fontSize: 12, textColor: 50 },
            });

            // Total Amount
            const finalY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            
            const totalAmount = invoice.isStripeInvoice 
                ? (invoice.total / 100).toFixed(2)
                : Number(invoice.amount).toFixed(2);
                
            doc.text(`Total: ${invoice.currency.toUpperCase()} ${totalAmount}`, 14, finalY);

            // Footer
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.text("Thank you for your business!", 14, finalY + 10);

            // Save PDF
            const invoiceNum = invoice.invoiceNumber || invoice.number || "unknown";
            doc.save(`invoice-${invoiceNum}.pdf`);
        } catch (error) {
            console.error("Error generating invoice PDF:", error);
        }
    };

    return (
        <div className={styles.planContainer}>
            <div className={styles.currentPlanHeader}>
                <p className={styles.planHeading}>{t('Invoices')}{` (${totalCount})`}</p>
                <div className={styles.toggleContainer}>
                    <label className={styles.switch}>
                        <span className={`${styles.slider} ${styles.round}`}></span>
                    </label>
                </div>
                {/* Dropdown for Status Filtering */}
                <div style={{}}>
                    <Select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        displayEmpty
                        variant="outlined"
                        sx={{
                            width: '100%',
                            padding: '0px',
                            '& .MuiSelect-select': {
                                padding: '5px',
                                border: 'none',
                                backgroundColor: 'transparent',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                        }}
                    >
                        <MenuItem value="">{t('general.UI.all')}</MenuItem>
                        <MenuItem value="paid">{t('status.paid')}</MenuItem>
                        <MenuItem value="unpaid">{t('status.unpaid')}</MenuItem>
                        <MenuItem value="cancel">{t('status.cancel')}</MenuItem>
                        <MenuItem value="refunded">{t('status.refunded')}</MenuItem>
                    </Select>
                </div>
            </div>
            <Box sx={{ width: '100%', height: "100%" }}>
                <Paper sx={{
                    width: '100%',
                    mb: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    boxShadow: 'none',
                    borderRadius: 0,
                    border: 'none'
                }}>
                    <TableContainer sx={{
                        maxHeight: 400,
                        maxHeight: 400,
                        '&::-webkit-scrollbar': {
                            width: 0,
                        },
                        '-ms-overflow-style': 'none',
                        'scrollbar-width': 'none',
                    }}>
                        <Table size={dense ? 'small' : 'medium'}>
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                setSelectedStatus={setSelectedStatus}
                                selectedStatus={selectedStatus}
                            />

                            <TableBody>
                                {visibleRows?.length > 0 ? (
                                    visibleRows.map((row, index) => {
                                        const invoiceNumber = row.invoiceNumber || (row.number ? row.number.slice(9) : '');
                                        const createdDate = row.isStripeInvoice 
                                            ? moment.unix(row.created).format('dddd, MMMM Do, YYYY')
                                            : moment(row.createdAt).format('dddd, MMMM Do, YYYY');
                                        const dueDate = row.isStripeInvoice && row.due_date 
                                            ? moment.unix(row.due_date).format('dddd, MMMM Do, YYYY') 
                                            : row.dueDate 
                                                ? moment(row.dueDate).format('dddd, MMMM Do, YYYY') 
                                                : t('noDueDate');
                                        const amount = row.isStripeInvoice ? row.total / 100 : row.amount;
                                        const status = row.isStripeInvoice 
                                            ? (row.paid ? t('status.paid') : row.status === 'void' ? t('status.cancel') : row.status === 'refunded' ? t('status.refunded') : t('status.unpaid'))
                                            : (row.status === 'paid' ? t('status.paid') : row.status === 'void' ? t('status.cancel') : row.status === 'refunded' ? t('status.refunded') : t('status.unpaid'));
                                        
                                        return (
                                            <TableRow
                                                onClick={() => handleRowClick(row)} // Handle row click
                                                key={index.toString()} sx={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8fcfd' }}>
                                                <TableCell component="th" scope="row">
                                                    {invoiceNumber}
                                                </TableCell>
                                                <TableCell>{createdDate}</TableCell>
                                                <TableCell >{dueDate}</TableCell>
                                                <TableCell padding='none'>{getCurrencySymbol(row.currency)} {amount} {row.currency.toUpperCase()}</TableCell>
                                                <TableCell>
                                                    <div className={`${styles.status} ${row.paid || row.status === 'paid' ? styles.paidStatus : styles.unpaidStatus}`}>
                                                        {status}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent row click when clicking the icon
                                                            handleDownloadInvoice(row);
                                                        }}
                                                        aria-label={t('download')}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                ) : (
                                    // Display a message when no invoices are found
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                {t('general.UI.noInvoiceMsg')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={filteredCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        labelRowsPerPage={t('pagination.rowsPerPage')}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('pagination.of')} ${count}`}
                    />
                </Paper>
            </Box>
            {selectedInvoice && (
                <InvoiceFullView
                    invoice={selectedInvoice}
                    onClose={handleCloseFullView}
                />
            )}
        </div>
    );
};

export default InvoiceUi;


